/* ── Game registry ─────────────────────────────────────────── */
/* Multi-game type support: each game type implements               */
/* GameDefinition<TSession> and registers with the GameRegistry.   */

import type { GameResultData, PlayerViewSnapshot } from '@everyturn/protocol';
import type { SessionContext, PendingDecisionIdentity, LogEntry } from './generator-types.js';
import type { HudInstruction, HudState, ChipLegendEntry } from './hud-instructions.js';
import type { GameLayout } from './resolve-layout.js';
import type { ViewInstruction } from './view-instructions.js';

/* ── Types ────────────────────────────────────────────────── */

/** Result of validating a player action. */
export interface ValidationResult {
  valid: boolean;
  error?: string;
}

export type SessionOutcome<TResult = unknown> =
  | { status: 'running' }
  | { status: 'completed'; result: TResult }
  | { status: 'failed'; error: Error };

/** Per-seat instruction bundles produced after an action. */
export interface PerSeatInstructions {
  /** View instructions keyed by seat id (e.g. 'north', 'south'). */
  view: Record<string, ViewInstruction[]>;
  /** When set, the transport should swap the player from one seat to another. */
  seatSwap?: { from: string; to: string };
}

/** Snapshot used for reconnection / late-join. */
export interface PlayerView extends PlayerViewSnapshot {
  hud: HudState;
}

/** A game action sent over the wire, opaque at the registry level. */
export type GameAction = Record<string, unknown>;

export interface ReplayCheckpoint {
  event: Exclude<LogEntry, { type: 'chance' | 'op' }> | null;
  seatSwaps: { from: string; to: string }[];
}

/** Post-game action identifiers. */
export type EndScreenAction = 'playAgain' | 'backToLobby' | 'rematch';

/** Seat info passed to end-screen renderers. */
export type EndScreenSeatInfo = Record<string, { userId: string; displayName: string; isBot: boolean }>;

/**
 * Configuration for the end-game result screen.
 * The framework calls `renderResult` on game end and emits the returned
 * HUD instructions — no game-specific parsing needed in the client.
 */
export interface EndScreenConfig {
  /** Convert game result + seat info into HUD instructions for the result overlay. */
  renderResult(result: unknown, seats: EndScreenSeatInfo): HudInstruction[];
  /** Post-game action buttons to show (rendered by the HUD system). */
  actions?: EndScreenAction[];
}

/** Everything a bot sees when asked to act — same info a human player has. */
export interface BotContext {
  /** The bot's seat. */
  seat: string;
  /** What the game is asking (e.g. 'makeBid', 'playCard'). */
  prompt: string;
  /** Legal actions the bot may choose from. */
  legal: unknown[];
  /** Game-specific context supplied by the game author via askPlayer. */
  data?: Record<string, unknown>;
  /** The bot's view of the game — same snapshot a human client sees. */
  view: PlayerView;
}

/** Bot interface — receives only what a human player would see. */
export interface GameBot {
  pickAction(context: BotContext): GameAction;
}

/* ── StatsPanelConfig ────────────────────────────────────── */

/**
 * Declares a game-specific stats / scoring panel that can be opened from
 * the bottom-bar stats button in the IDE layout designer.
 *
 * The `componentId` is an opaque string that the IDE resolves against its
 * `STATS_PANEL_REGISTRY` (a map from id → React component).  Games that
 * want a custom panel set this field; games that don't need one leave it
 * undefined and the stats button remains decorative.
 */
export interface StatsPanelConfig {
  /** Identifier used by the IDE to look up the React component to render. */
  componentId: string;
  /** Display title for the panel header.  Defaults to 'Stats'. */
  title?: string;
}

/* ── GameMeta — shared declarative fields ────────────────── */

/**
 * Declarative game metadata shared between {@link GeneratorGameConfig}
 * (game-author input) and {@link GameDefinition} (framework interface).
 */
export interface GameMeta {
  /** Unique game identifier (e.g. 'bridge', 'hearts'). */
  gameId: string;
  /** Human-readable name. */
  displayName: string;
  /** Seat identifiers in turn order. */
  seats: readonly string[];
  /** Fixed partnerships keyed by seat; omitted for individual games. */
  seatTeams?: Readonly<Record<string, string>>;
  /** Layout specification (landscape/portrait templates, param resolution). */
  layout: GameLayout;
  /**
   * Optional layout source identifier (e.g. 'prime-bridge').
   * Used by the IDE to link to the layout designer for this game.
   * Does not affect runtime behaviour.
   */
  layoutId?: string;
  /** Optional end-game screen configuration. */
  endScreen?: EndScreenConfig;
  /** Minimum players. */
  minPlayers?: number;
  /** Maximum players. */
  maxPlayers?: number;
  /** Default seat for the local player (e.g. in the IDE). Defaults to `seats[0]`. */
  defaultSeat?: string;
  /** Default session options for quick-start / IDE use. */
  defaultSessionOpts?: unknown;
  /** Structured convention card shown to players (e.g. bidding system used by bots). */
  conventions?: ConventionCard;
  /** Describes the match format / scoring variant for multi-round games. */
  matchFormat?: MatchFormatCard;
  /**
   * Ordered info cards shown as tabs in the game info dialog.
   * This is the canonical source of all info content. When not provided,
   * the framework auto-builds it from `conventions`, `matchFormat`, and
   * the layout's bottom-bar `helpPanel` (if present).
   */
  infoCards?: InfoCard[];
  /**
   * Optional custom stats / scoring panel for this game.
   * When set, the stats button in the IDE layout designer bottom bar opens
   * a panel rendered by the component identified by {@link StatsPanelConfig.componentId}.
   */
  statsPanel?: StatsPanelConfig;
}

/* ── GameDefinition ─────────────────────────────────────── */

export interface GameDefinition<TSession = unknown> extends GameMeta {
  /** Restore through canonical replay, observing settled timeline boundaries. Observers must not mutate the session. */
  restoreTimeline?(data: unknown, onCheckpoint: (session: TSession, checkpoint: ReplayCheckpoint) => void): TSession;
  sessionControl?:
    | { kind: 'transport-owned'; invalidateRequests(session: TSession): void }
    | {
        kind: 'session-owned';
        getPendingSeatSwap(session: TSession): { from: string; to: string } | undefined;
        getContext(session: TSession): SessionContext;
        setBotSeat(session: TSession, seat: string, isBot: boolean): void;
        swapSeats(session: TSession, from: string, to: string): void;
        getDecisionIdentity(session: TSession, seat: string): PendingDecisionIdentity | null;
        fork(session: TSession): void;
      };
  minPlayers: number;
  maxPlayers: number;

  /** Create a new game session with the given options. */
  createSession(
    opts: unknown,
    botSeats?: ReadonlySet<string>,
    seatDisplayNames?: ReadonlyMap<string, string>,
  ): TSession;

  /** Create a bot for this game type. */
  createBot(): GameBot;

  /** Validate a player action before applying it. */
  validateAction(session: TSession, seat: string, action: GameAction, requestId: unknown): ValidationResult;

  /** Apply a validated action. Returns an opaque result passed to deriveInstructions. */
  applyAction(session: TSession, seat: string, action: GameAction): unknown;

  /** Derive per-seat view+gui instructions after an action is applied. */
  deriveInstructions(session: TSession, seat: string, action: GameAction, actionResult: unknown): PerSeatInstructions;

  /** Get a reconnection snapshot for a specific seat. */
  getPlayerView(session: TSession, seat: string): PlayerView;

  /**
   * Build the context a bot needs to pick an action for a seat.
   * Returns `null` when no action is pending for that seat.
   */
  getBotContext(session: TSession, seat: string): BotContext | null;

  /** Return a detached decoded decision log for observability. */
  getDecisionLog?(session: TSession): LogEntry[];

  /** Current annotated hand/deal, re-derived during replay; absent for games without round markers. */
  getRoundNumber?(session: TSession): number | undefined;

  /** Serialize session state for DB persistence. */
  serializeSession(session: TSession): unknown;

  /** Deserialize session state from DB. */
  deserializeSession(data: unknown): TSession;

  getOutcome(session: TSession): SessionOutcome;
  /** Pure, public result facts; only called for a successful terminal session. */
  projectResult?(session: TSession): GameResultData;

  /** True when the game is over. */
  isFinished(session: TSession): boolean;

  /** Return the game result (undefined until finished). */
  getResult(session: TSession): unknown | undefined;

  /**
   * Return the seat that should act next, or `null` when no player can act.
   * When provided, LocalTransport uses this instead of probing every bot seat.
   */
  getActivePlayer?(session: TSession): string | null;

  /**
   * Return the set of legal actions for the active player, or `null`
   * when no player can act or legal actions are unavailable.
   */
  getLegalActions?(session: TSession): unknown[] | null;
}

/* ── Info section content model (shared by ConventionCard & MatchFormatCard) ── */

/** A bullet-list item with optional sub-items. */
export interface InfoListItem {
  type?: 'list';
  text: string;
  sub?: string[];
}

/** A table block rendered inline within a section. */
export interface InfoTableItem {
  type: 'table';
  columns: string[];
  rows: string[][];
}

export type InfoSectionItem = InfoListItem | InfoTableItem;

/** A titled section containing mixed list/table content. */
export interface InfoSection {
  header: string;
  items: InfoSectionItem[];
}

/** Structured data describing the conventions / rules a game's bots follow. */
export interface ConventionCard {
  title: string;
  sections: InfoSection[];
}

/** Describes the format/scoring variant of a multi-round game. */
export interface MatchFormatCard {
  /** Short label shown in UI badges/headers (e.g. "Chicago (4-Deal)"). */
  label: string;
  /** Brief one-line description. */
  summary: string;
  /** Detailed sections using the shared info section model. */
  sections: InfoSection[];
}

/* ── Info cards — unified content model for the game info dialog ── */

/** An info card showing structured sections (rules, conventions, match format, etc.). */
export interface SectionsInfoCard {
  id: string;
  /** Tab label in the info dialog. */
  label: string;
  /** Optional subtitle shown below the header. */
  summary?: string;
  type: 'sections';
  sections: InfoSection[];
}

/** An info card showing a chip legend (player status indicators). */
export interface ChipLegendInfoCard {
  id: string;
  /** Tab label in the info dialog. */
  label: string;
  type: 'chipLegend';
  chipLegend: ChipLegendEntry[];
}

/**
 * A single tab in the game info dialog.
 * Games define an ordered array of these; the dialog renders them as tabs.
 */
export type InfoCard = SectionsInfoCard | ChipLegendInfoCard;

/* ── Registry ─────────────────────────────────────────────── */

export class GameRegistry {
  private definitions = new Map<string, GameDefinition>();

  register(definition: GameDefinition): void {
    if (this.definitions.has(definition.gameId)) {
      throw new Error(`Game type '${definition.gameId}' already registered`);
    }
    this.definitions.set(definition.gameId, definition);
  }

  get(gameId: string): GameDefinition | undefined {
    return this.definitions.get(gameId);
  }

  has(gameId: string): boolean {
    return this.definitions.has(gameId);
  }

  listGameIds(): string[] {
    return [...this.definitions.keys()];
  }
}
